import React, { Activity, Children, useMemo, useRef } from 'react';

import { useScrollMemory } from '@/hooks/useScrollMemory';

export interface ActivityStepWizardProps {
  /** Step content as children - each child will be wrapped in Activity component */
  children: React.ReactNode;
  scrollMemory?: Parameters<typeof useScrollMemory>[0];
  /** Optional ref for the scrollable container. If not provided, creates its own */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Active step from parent (required) */
  activeStep: number;
  /** Optional wrapper className */
  className?: string;
}

/**
 * ActivityStepWizard - A reusable step wizard component using React 19 Activity
 * with automatic scroll memory for modal body scrolling.
 *
 * Features:
 * - Wraps each step in React 19 Activity component (preserves state/DOM)
 * - Automatic scroll position memory per step context
 * - Controlled component - parent manages activeStep state
 *
 *
 * Example:
 *
 * const scrollMemoryKey = useMemo(() => {
 *   return createStepKey(activeStep, stepData);
 * }, [activeStep, stepData]);
 *
 *
 * <ActivityStepWizard
 *   scrollMemory={{
 *     containerRef: modalBodyRef,
 *     key: scrollMemoryKey,
 *   }}
 * >
 *   <StepOneCategories />
 *   <StepTwoSubcategories />
 *   <StepThreeSearchResults />
 *   <StepFourDocumentPreview />
 * </ActivityStepWizard>
 *
 * @param children - The steps to render.
 * @param scrollMemory - The scroll memory to use.
 * @returns
 */
const ActivityStepWizard: React.FC<ActivityStepWizardProps> = ({
  children,
  scrollMemory,
  activeStep,
  className,
}) => {
  const internalContainerRef = useRef<HTMLDivElement>(null);

  // Convert children to array
  const stepsArray = useMemo(() => Children.toArray(children), [children]);

  // Integrate scroll memory
  useScrollMemory(scrollMemory);

  return (
    <div ref={internalContainerRef} className={className}>
      {stepsArray.map((step, index) => {
        const stepNumber = index + 1;
        // Include stepData in key so Activity remounts when content changes
        const activityKey = `${scrollMemory?.key}-${stepNumber}`;
        return (
          <Activity
            key={activityKey}
            mode={activeStep === stepNumber ? 'visible' : 'hidden'}
          >
            {step}
          </Activity>
        );
      })}
    </div>
  );
};

export default ActivityStepWizard;
